import { QuizQuestion } from './quizzes';

export const cellParameterTooltips = {
  tau_in: {
    title: 'τ_in (Tau In)',
    content: `Controls the speed of depolarization (upstroke velocity). Represents fast inward Na+ current activation in real cardiac cells. Lower values = faster depolarization. Range: 0.1-1.0`,
    physiological: 'Corresponds to sodium channel activation kinetics'
  },
  tau_out: {
    title: 'τ_out (Tau Out)',
    content: `Controls the speed of repolarization. Represents K+ outward current in real cardiac cells. Higher values = longer action potential duration. Range: 1.0-15.0`,
    physiological: 'Corresponds to potassium channel kinetics'
  },
  tau_open: {
    title: 'τ_open (Tau Open)',
    content: `Controls the recovery time for the h-gate. Higher values = longer refractory period. Range: 50-200`,
    physiological: 'Corresponds to recovery from inactivation of sodium channels'
  },
  tau_close: {
    title: 'τ_close (Tau Close)',
    content: `Controls the inactivation time for the h-gate. Lower values = faster inactivation. Range: 50-150`,
    physiological: 'Corresponds to sodium channel inactivation kinetics'
  },
  v_gate: {
    title: 'V_gate',
    content: `Threshold voltage for gate dynamics. Higher values = higher threshold for activation. Range: 0.05-0.3`,
    physiological: 'Corresponds to voltage threshold for sodium channel inactivation'
  }
};

export const actionPotentialEducation = {
  basic: {
    title: 'Action Potential Basics',
    content: `
      <p>An action potential is a rapid change in membrane potential that occurs in excitable cells like cardiac myocytes. The cardiac action potential has several distinct phases:</p>
      <ol>
        <li><strong>Phase 0:</strong> Rapid depolarization (upstroke)</li>
        <li><strong>Phase 1:</strong> Early repolarization</li>
        <li><strong>Phase 2:</strong> Plateau</li>
        <li><strong>Phase 3:</strong> Repolarization</li>
        <li><strong>Phase 4:</strong> Resting potential</li>
      </ol>
      <p>The Mitchell Schaeffer model simplifies these dynamics while maintaining the essential features.</p>
    `
  },
  intermediate: {
    title: 'Ion Channels and Currents',
    content: `
      <p>The cardiac action potential is generated by the coordinated activity of multiple ion channels:</p>
      <ul>
        <li><strong>Phase 0:</strong> Voltage-gated Na+ channels open, causing rapid influx of Na+</li>
        <li><strong>Phase 1:</strong> Transient outward K+ current (I<sub>to</sub>)</li>
        <li><strong>Phase 2:</strong> Balance between inward Ca2+ current (I<sub>Ca,L</sub>) and outward K+ currents</li>
        <li><strong>Phase 3:</strong> Delayed rectifier K+ currents (I<sub>Kr</sub>, I<sub>Ks</sub>) and inward rectifier K+ current (I<sub>K1</sub>)</li>
        <li><strong>Phase 4:</strong> Maintained by Na+/K+ ATPase and I<sub>K1</sub></li>
      </ul>
      <p>In the Mitchell Schaeffer model, these currents are simplified into fast inward (J<sub>in</sub>) and slow outward (J<sub>out</sub>) currents.</p>
    `
  },
  advanced: {
    title: 'Mitchell Schaeffer Model Equations',
    content: `
      <p>The Mitchell Schaeffer model is a phenomenological model with two variables:</p>
      <div style="background-color: #f8f8f8; padding: 10px; border-radius: 4px; margin: 10px 0;">
        <p>dv/dt = J<sub>in</sub> + J<sub>out</sub> + J<sub>stim</sub></p>
        <p>where:</p>
        <ul>
          <li>J<sub>in</sub> = (h × v² × (1-v)) / τ<sub>in</sub> (fast inward current)</li>
          <li>J<sub>out</sub> = -v / τ<sub>out</sub> (slow outward current)</li>
        </ul>
        <p>dh/dt = (1-h) / τ<sub>open</sub> if v < v<sub>gate</sub>, otherwise -h / τ<sub>close</sub></p>
      </div>
      <p>Variables:</p>
      <ul>
        <li><strong>v:</strong> Normalized membrane voltage (0 to 1)</li>
        <li><strong>h:</strong> Inactivation gate variable (0 to 1)</li>
      </ul>
      <p>This model is particularly useful because its parameters have clear physiological interpretations.</p>
    `
  },
  clinical: {
    title: 'Clinical Relevance',
    content: `
      <p>Understanding action potential dynamics has direct clinical applications:</p>
      <ul>
        <li><strong>Antiarrhythmic drugs:</strong> Modify specific ion channels to alter AP properties</li>
        <li><strong>Class I drugs:</strong> Block Na+ channels (increase τ<sub>in</sub>, slowing depolarization)</li>
        <li><strong>Class III drugs:</strong> Block K+ channels (increase τ<sub>out</sub>)</li>
        <li><strong>Channelopathies:</strong> Genetic disorders affecting ion channels</li>
        <ul>
          <li>Long QT Syndrome: Prolonged repolarization (increased τ<sub>out</sub>)</li>
          <li>Brugada Syndrome: Abnormal Na+ channel function (altered τ<sub>in</sub>)</li>
        </ul>
      </ul>
      <p>Simulation of action potentials helps predict drug effects and understand disease mechanisms.</p>
    `
  }
};

export const cellModuleQuizQuestions: QuizQuestion[] = [
  {
    question: "Which parameter in the Mitchell-Schaeffer model primarily controls the upstroke velocity?",
    options: ["tau_in", "tau_out", "tau_open", "v_gate"],
    correctIndex: 0,
    explanation: "tau_in controls the fast inward current (J_in), which is responsible for the upstroke (depolarization) phase.",
    difficulty: "basic" as const
  },
  {
    question: "What happens to the action potential duration (APD) when tau_out is increased?",
    options: ["APD increases", "APD decreases", "APD remains unchanged", "APD becomes variable"],
    correctIndex: 0,
    explanation: "Increasing tau_out slows repolarization, which extends the action potential duration.",
    difficulty: "basic" as const
  },
  {
    question: "In the Mitchell-Schaeffer model, which variable represents the inactivation gate?",
    options: ["v", "h", "J_in", "J_out"],
    correctIndex: 1,
    explanation: "The 'h' variable represents the inactivation gate, which controls availability of the fast inward current.",
    difficulty: "intermediate" as const
  },
  {
    question: "What physiological structure does the J_in current in the Mitchell-Schaeffer model primarily represent?",
    options: ["Sodium channels", "Potassium channels", "Calcium channels", "Chloride channels"],
    correctIndex: 0,
    explanation: "J_in represents the fast inward current, which physiologically corresponds to sodium channel activation during depolarization.",
    difficulty: "intermediate" as const
  },
  {
    question: "In terms of clinical relevance, which class of antiarrhythmic drugs would be modeled by increasing tau_out?",
    options: ["Class I", "Class II", "Class III", "Class IV"],
    correctIndex: 2,
    explanation: "Class III antiarrhythmic drugs block potassium channels, prolonging repolarization, which is equivalent to increasing tau_out in the model.",
    difficulty: "advanced" as const
  }
];

export const stimulusParameterTooltips = {
  amplitude: {
    title: 'Stimulus Amplitude',
    content: `Controls the strength of the applied stimulus. Higher values = stronger stimulus that more easily initiates an action potential. Range: 0.5-2.0`,
    physiological: 'Corresponds to stimulus current in experimental settings'
  },
  duration: {
    title: 'Stimulus Duration',
    content: `Controls how long the stimulus is applied. Longer durations = more charge delivered. Range: 0.5-5.0`,
    physiological: 'Corresponds to pulse width in pacing devices'
  },
  startTime: {
    title: 'Start Time',
    content: `Controls when the stimulus is applied. Adjust to see effects on different phases of the action potential. Range: 0-50`,
    physiological: 'Corresponds to timing of electrical stimulation'
  }
};

export const s2StimulusTooltips = {
  s2Enabled: {
    title: 'S2 Stimulus (Premature Stimulus)',
    content: `Enable a second stimulus to study refractory properties. The S1-S2 protocol applies a conditioning stimulus (S1) followed by a premature stimulus (S2) at a variable coupling interval.`,
    physiological: 'Used clinically to assess vulnerability to arrhythmias and determine refractory periods'
  },
  s2Amplitude: {
    title: 'S2 Amplitude',
    content: `Strength of the premature (S2) stimulus. Higher values make it easier to capture the cell even during partial recovery. Range: 0.5-2.0`,
    physiological: 'Higher stimulus strength can overcome partial refractoriness'
  },
  s2Duration: {
    title: 'S2 Duration',
    content: `Duration of the S2 stimulus pulse. Longer durations deliver more charge. Range: 0.5-5.0`,
    physiological: 'Similar to S1 duration but applied during recovery phase'
  },
  s2StartTime: {
    title: 'S2 Coupling Interval',
    content: `When to apply the S2 stimulus. The coupling interval (S2 start - S1 start) determines whether the cell has recovered enough to respond. Short intervals during the absolute refractory period will fail to capture. Range: 50-500`,
    physiological: 'Determines if stimulus falls during absolute, relative, or supernormal period'
  }
}; 